import {
  type EdgeEntityData,
  type EdgeEntityDataResponse,
  safeAbsurd,
} from '@ipfabric/utils';
import type { DeviceViewEdgeEntity } from 'components/DeviceView/deviceViewTypes';
import { hasEntityInvalidEdgeProtocol } from '../../helpers/hasEntityInvalidEdgeProtocol';

import type { DeviceViewCommandFactory } from '../DeviceViewModel';
import { closeDeviceViewEntity } from './closeDeviceViewEntity';
import { showTable } from './showTable';

export interface LoadDeviceViewEdgeSettingsCommand {
  deviceViewEntity: DeviceViewEdgeEntity;
}

export const loadDeviceViewEdgeSettings: DeviceViewCommandFactory<
  LoadDeviceViewEdgeSettingsCommand
> = (cmdContext, dispatch) => {
  const getEdgeEntityData = async (
    deviceViewEntity: DeviceViewEdgeEntity,
  ): Promise<EdgeEntityData[]> => {
    switch (deviceViewEntity.type) {
      case 'topology':
        return await cmdContext.api.request<EdgeEntityDataResponse>({
          url: '/graphs/edges',
          method: 'POST',
          data: deviceViewEntity.children,
        });
      case 'pathLookup':
        return [
          {
            id: deviceViewEntity.id,
            source: {
              sn: deviceViewEntity.sourceSn,
              intName: deviceViewEntity.sourceIfaceName,
            },
            target: {
              sn: deviceViewEntity.targetSn,
              intName: deviceViewEntity.targetIfaceName,
            },
          },
        ];
      default:
        safeAbsurd(deviceViewEntity.type);
        return [];
    }
  };

  return async (command) => {
    const edges = await getEdgeEntityData(command.deviceViewEntity);

    if (edges) {
      const edgesHaveInvalidProtocol = hasEntityInvalidEdgeProtocol(edges);
      if (edgesHaveInvalidProtocol) {
        dispatch(closeDeviceViewEntity, {
          deviceViewEntityId: command.deviceViewEntity.id,
        });
        return;
      }
      cmdContext.updateState((draft) => {
        const foundEntity = draft.deviceViewEntities.find(
          (entity) => entity.id === command.deviceViewEntity.id,
        );
        if (foundEntity?.deviceViewElement === 'edge') {
          foundEntity.edges = edges;
          foundEntity.loaded = true;
        }
      });

      const { deviceViewEntities } = cmdContext.getState();
      for (const deviceViewEntity of deviceViewEntities) {
        if (
          deviceViewEntity?.deviceViewElement === 'edge' &&
          deviceViewEntity.redirectToTable !== undefined
        ) {
          dispatch(showTable, {
            deviceViewEntityId: deviceViewEntity.id,
            webEndpoint: deviceViewEntity.redirectToTable,
          });
        }
      }
    }
  };
};
